import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { Button, Grid, Paper, TextField, Typography } from '@material-ui/core';

import API from 'utils/api';
import { useAuth, useSources } from 'hooks';
import { AutoComplete, ErrorDialog, Form } from 'components';
import { MultiSelect, PasswordField, ButtonContainer } from 'components';
import { getRoles, ROLES } from './roles';

import styles from './UserForm.module.scss';

const initialValues = {
  name: '',
  email: '',
  phone: '',
  password: '',
  passwordConfirm: '',
  group: '',
  roles: [],
  influencerCode: '',
};

const validate = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().required('Required').email('Invalid email address'),
  phone: Yup.string().required('Required'),
  password: Yup.string()
    .required('Required')
    .min(6, 'Password must be at least 6 characters long'),
  passwordConfirm: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('password')], 'Password confirmation does not match'),
  group: Yup.string().required('Required'),
  roles: Yup.array().of(Yup.string()).min(1, 'Select at least one role'),
  influencerCode: Yup.string().when('roles', {
    is: roles => roles.includes(ROLES.INFLUENCER),
    then: schema => schema.required('Required'),
  }),
});

const UserForm = props => {
  const [groups, setGroups] = useState([]);
  const [dialog, setDialog] = useState({});

  const auth = useAuth();
  const sources = useSources();
  const roles = getRoles(auth);

  useEffect(() => {
    API.getGroups({ select: 'name' }, sources).then(({ data }) => {
      if (data && data.docs) {
        setGroups(data.docs);
      }
    });
  }, [sources]);

  const onSubmit = async (values, { setSubmitting }) => {
    const { error } = await API.createUser(
      {
        fullname: values.name,
        email: values.email,
        password: values.password,
        phone: values.phone,
        roles: values.roles,
        group: values.group,
        influencerCode: values.influencerCode || undefined,
      },
      sources
    );

    setSubmitting(false);

    if (error) {
      setDialog({ error });
    } else {
      props.history.push('/dashboard/users');
    }
  };

  const onCloseDialog = () => setDialog({});

  return (
    <div className={styles.userForm}>
      <Typography variant="h6" gutterBottom>
        Create new user
      </Typography>

      <Form validationSchema={validate} initialValues={initialValues} onSubmit={onSubmit}>
        {({ fieldProps, isSubmitting, values }) => (
          <Grid item xs={12}>
            <Paper className={styles.paper}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField {...fieldProps('name', 'Full Name')} />
                </Grid>

                <Grid item xs={6}>
                  <TextField {...fieldProps('email', 'Email')} />
                </Grid>

                <Grid item xs={4}>
                  <TextField {...fieldProps('phone', 'Phone')} />
                </Grid>

                <Grid item xs={8} lg={8} />

                <Grid item xs={4}>
                  <PasswordField {...fieldProps('password', 'Password')} />
                </Grid>

                <Grid item xs={4}>
                  <PasswordField {...fieldProps('passwordConfirm', 'Confirm Password')} />
                </Grid>

                <Grid item xs={4} lg={4} />
              </Grid>
            </Paper>

            <Paper className={styles.paper}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <AutoComplete
                    {...fieldProps('group', 'Group')}
                    items={groups.map(({ _id: id, name: text }) => ({
                      id,
                      text,
                    }))}
                  />
                </Grid>

                <Grid item xs={4}>
                  <MultiSelect {...fieldProps('roles', 'Roles')} options={roles} />
                </Grid>

                <Grid item xs={4}>
                  {values.roles.includes(ROLES.INFLUENCER) ? (
                    <TextField {...fieldProps('influencerCode', 'Influencer Code')} />
                  ) : null}
                </Grid>
              </Grid>
            </Paper>

            <ButtonContainer>
              <Button component={Link} to="/dashboard/users">
                cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                create
              </Button>
            </ButtonContainer>
          </Grid>
        )}
      </Form>

      <ErrorDialog open={!!dialog.error} onClose={onCloseDialog} message={dialog.error} />
    </div>
  );
};

export default UserForm;
